/**
 * RowActionItem — v3 button for a single action inside MRT's
 * `renderRowActionMenuItems` slot.
 *
 * Visually matches the items inside @shared/ui/Popover/ActionMenu
 * (same `.qhs-pop__item` class system from Popover.css). Designed
 * to render inside MRT's own row-action menu portal, so it does
 * NOT include its own Popover wrapper — it expects to live inside
 * a parent menu container.
 *
 * Props:
 *   - title: string                      visible label
 *   - icon: ReactNode                    leading icon (svg)
 *   - onClick: (event) => void           handler; receives the click event
 *   - setAnchorEl?: (null) => void       MRT passes this to close the menu;
 *                                        called automatically after onClick
 *   - disabled?: boolean                 explicit disabled flag
 *   - isLoading?: boolean                shows an inline spinner; while
 *                                        loading the button is disabled
 *                                        and the menu is NOT auto-closed
 *                                        (so the user sees the spinner)
 *   - danger?: boolean                   destructive style (red)
 *   - tooltip?: ReactNode                shown only while the item is
 *                                        disabled — typically a
 *                                        permission-gated hint such as
 *                                        "You don't have rights for this
 *                                        action"
 *
 * Convenience props for table integration:
 *   - isDisabledFn?: (rowUuid) => bool   per-row disabled lookup; if
 *                                        provided, takes precedence over
 *                                        `disabled` and is called with `rowUuid`
 *   - rowUuid?: string                   passed to isDisabledFn
 *
 * Skips rendering when `showCondition` is explicitly `false` (lets
 * legacy callers gate visibility without wrapping in `{cond && ...}`).
 */
import { forwardRef, useMemo } from 'react';
import { Tooltip } from '@shared/ui/Tooltip';
import '@shared/ui/Popover/Popover.css';

const Spinner = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
    style={{ animation: 'qhs-row-action-spin 0.9s linear infinite' }}
  >
    <path d="M12 2v4" />
    <path d="M12 18v4" />
    <path d="M4.93 4.93l2.83 2.83" />
    <path d="M16.24 16.24l2.83 2.83" />
    <path d="M2 12h4" />
    <path d="M18 12h4" />
    <path d="M4.93 19.07l2.83-2.83" />
    <path d="M16.24 7.76l2.83-2.83" />
  </svg>
);

const RowActionItem = forwardRef(function RowActionItem(
  {
    title,
    icon,
    onClick,
    setAnchorEl,
    disabled,
    isLoading,
    danger,
    isDisabledFn,
    rowUuid,
    showCondition = true,
    tooltip,
  },
  ref
) {
  const computedDisabled = useMemo(() => {
    if (isLoading) return true;
    if (typeof isDisabledFn === 'function') return Boolean(isDisabledFn(rowUuid));
    return Boolean(disabled);
  }, [isLoading, isDisabledFn, rowUuid, disabled]);

  if (!showCondition) return null;

  const handleClick = event => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent?.stopImmediatePropagation?.();
    if (computedDisabled) return;
    const result = onClick?.(event);
    // Async actions (mutations, PDF downloads) return a promise. Closing
    // the menu synchronously unmounts this item mid-request, which aborts
    // the in-flight fetch (react-fetching-library aborts on unmount) — the
    // reason a "No Show" POST showed up as canceled in the console. Keep
    // the menu (and this component) mounted until the promise settles, then
    // close; the item shows its spinner via `isLoading` meanwhile. Sync
    // actions (navigation, opening a dialog) return undefined and close the
    // menu immediately, as before.
    if (result && typeof result.then === 'function') {
      result.then(
        () => setAnchorEl?.(null),
        () => setAnchorEl?.(null)
      );
    } else {
      setAnchorEl?.(null);
    }
  };

  const className = `qhs-pop__item${danger ? ' is-danger' : ''}`;

  const button = (
    <button
      ref={ref}
      type="button"
      className={className}
      disabled={computedDisabled}
      onClick={handleClick}
    >
      {/* Icon-less items (group sub-options) still need a visible
          spinner while their async action is in flight. */}
      {(icon || isLoading) && (
        <span className="qhs-pop__item-icon">{isLoading ? <Spinner /> : icon}</span>
      )}
      <span className="qhs-pop__item-label">{title}</span>
      <style>{`
        @keyframes qhs-row-action-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );

  // Show the tooltip only when the action is disabled for a REASON —
  // an availability rule or a permission gate. While a request is in
  // flight (`isLoading`) the item is disabled too, but the spinner
  // already explains why, and the reason text ("Available for …")
  // would be wrong for a row that IS eligible — so loading suppresses
  // the tooltip. MUI Tooltip ignores hover events on disabled
  // descendants, so the disabled <button> is wrapped in a <span> per
  // MUI's documented workaround.
  // Placed to the left: on top it covered the item above, which could
  // then not be clicked while the hint was up.
  if (tooltip && computedDisabled && !isLoading) {
    return (
      <Tooltip title={tooltip} placement="left">
        <span style={{ display: 'block' }}>{button}</span>
      </Tooltip>
    );
  }

  return button;
});

export default RowActionItem;
